import Clarity from "@microsoft/clarity";
import { jwtDecode } from "jwt-decode";
import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { Dispatch } from "redux";
import { EXTERNAL_LOGIN_URL } from "../consts";
import { LexTokenPayload } from "../models";
import { doLogout, loginWithToken } from "../store/reducer/_sessionReducer";
import { RootState } from "../store/reducer/_store";
import { removeLoggedStatus, removeLoggedUser, removeToken } from "../utils";

export function useValidateToken() {
  const dispatch = useDispatch();

  const token = useSelector((state: RootState) => state.sessionContext.token);
  const loggedUser = useSelector(
    (state: RootState) => state.sessionContext.loggedUser,
  );

  const { token: tokenFromRoute } = useParams<{ token?: string }>();

  const isAuthRoute = window.location.pathname.startsWith("/autenticar");

  const hasValidatedRef = useRef(false);

  const handleInvalidToken = useCallback(
    (dispatch: Dispatch) => {
      removeLoggedStatus();
      removeLoggedUser();
      removeToken();
      dispatch(doLogout());
      if (!isAuthRoute) window.location.href = EXTERNAL_LOGIN_URL;
    },
    [isAuthRoute],
  );

  const handleValid = useCallback(
    (token: string) => {
      try {
        const decoded = jwtDecode<LexTokenPayload>(token);
        const nowInSeconds = Date.now() / 1000;
        if (!decoded.exp || decoded.exp < nowInSeconds) {
          handleInvalidToken(dispatch);

          return;
        }

        Clarity.identify(String(decoded.dados.IDPessoa));
        Clarity.setTag("user_email", decoded.cliente.email);
        Clarity.setTag("user_origin", decoded.origem);

        Clarity.event("Login success");

        if (!loggedUser) {
          dispatch(
            loginWithToken({
              user: decoded.cliente,
              token,
            }),
          );
        }
      } catch {
        handleInvalidToken(dispatch);
      }
    },
    [dispatch, loggedUser, handleInvalidToken],
  );

  useEffect(() => {
    if (hasValidatedRef.current) return;
    hasValidatedRef.current = true;

    if (token) {
      handleValid(token);
      return;
    }

    if (!isAuthRoute) redirectToExternalLogin();
  }, [token, tokenFromRoute, handleValid, isAuthRoute]);

  function redirectToExternalLogin() {
    window.location.href = EXTERNAL_LOGIN_URL;
  }
}
